import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {IModule} from '../models/module/IModule';
import {IModuleMenu} from '../models/module/IModuleMenu';
import {CorporateFormsService} from '../corporate/services/corporate-forms.service';
import {NgxPermissionsService} from 'ngx-permissions';
import {TenantContextService} from './tenant-context.service';
import {TenantModuleCode} from '../models/tenant/tenant-bootstrap.model';
@Injectable()
export class ModulesService {

  private permissionsService = inject(NgxPermissionsService);
  private tenantContext = inject(TenantContextService);

  private static readonly modulePermissionMap: Record<number, string> = {
    1: 'CorporateUser',
    2: 'InvoicingUser',
    3: 'ComplianceUser'
  };

  // Maps the UI workspace id to the Billing module it's licensed under. Compliance (3) lives
  // inside the Corporate module. Only enforced when the tenant bootstrap has resolved.
  private static readonly moduleLicenseMap: Record<number, TenantModuleCode> = {
    1: TenantModuleCode.Corporate,
    2: TenantModuleCode.Invoicing,
    3: TenantModuleCode.Corporate
  };

  private modules$ = new BehaviorSubject( [
    {
      name: 'Home',
      id: 1,
      icon: 'assets/modules/home.svg',
      menu: []
    },
    {
      name: 'Finance',
      id: 2,
      icon: 'assets/modules/invoice.svg',
      menu: []
    },
    {
      name: 'Compliance',
      id: 3,
      icon: 'assets/modules/compliance.svg',
      menu: []
    },
    ]);

  private selectedModule = new BehaviorSubject( {} as IModule);
  public moduleName$ = new BehaviorSubject('');

  getModules(): Observable<IModule[]> {
    return this.modules$.asObservable();
  }


  setModuleName(name: string): void {
    this.moduleName$.next(name);
  }


  async hasModuleAccess(moduleId: number): Promise<boolean> {
    const requiredPermission = ModulesService.modulePermissionMap[moduleId];
    if (!requiredPermission) return false;

    const hasPermission = await this.permissionsService.hasPermission(requiredPermission);
    if (!hasPermission) return false;

    // When the tenant is known AND has a license with modules, also require the org to be licensed
    // for this module. If bootstrap never resolved, or the tenant has no licensed modules yet
    // (provisioned but no plan), we don't gate on licensing — permission alone decides.
    const licensed = this.tenantContext.bootstrap$?.entitlements?.modules ?? [];
    if (this.tenantContext.isResolved && licensed.length > 0) {
      const code = ModulesService.moduleLicenseMap[moduleId];
      if (code && !this.tenantContext.hasModule(code)) return false;
    }
    return true;
  }

  getFirstAccessibleModule(): Promise<number> {
    return this.hasModuleAccess(1).then(has1 => {
      if (has1) return 1;
      return this.hasModuleAccess(2).then(has2 => {
        if (has2) return 2;
        return this.hasModuleAccess(3).then(has3 => has3 ? 3 : 0);
      });
    });
  }

  selectModule(id: number) {
   const selected = this.modules$.getValue().find(m => m.id === id);
   sessionStorage.setItem('module', id.toString());
   localStorage.setItem('module', id.toString());
   this.selectedModule.next(selected);
  }

  getSelectedModule(): number {
    const selected = +(sessionStorage.getItem('module') || localStorage.getItem('module'));
    if (selected) {
      this.moduleName$.next(this.modules$.getValue().find(m => m.id === selected).name)
      this.selectedModule.next(this.modules$.getValue().find(m => m.id === selected));
    }
    return selected;
  }

}
